import { isNumber } from "lodash";
import { isDefined } from "react-migration/lib/util/isDefined";
import { getAttributeNumericValue, getAttributeValue } from "../../utils";
import { OutputAreaAttribute } from "../types";
import { ValueFormatter } from "../utils";
import { Color } from "d3-color";
import {
  SingleDesignation,
  SingleDesignationDesignationAttribute,
} from "react-migration/domains/constraints/typings/applicationTypes/SingleDesignation";
import clsx from "clsx";

export interface AttributeSummaryTableProps {
  designation: SingleDesignation;
  comparativeDesignation?: SingleDesignation;
  attributes: OutputAreaAttribute[];
  colorAccessor: (id: string) => Color;
  printable?: boolean;
}

export function AttributeSummaryTable({
  designation,
  attributes,
  comparativeDesignation,
  colorAccessor,
  printable = false,
}: AttributeSummaryTableProps) {
  const designationAttributes = designation.designation_attributes;
  const comparativeDesignationAttributes = comparativeDesignation?.designation_attributes;

  return (
    <div
      className={clsx("flex flex-col gap-y-1", {
        "w-[335px] print:justify-self-start shrink print:origin-left": printable,
      })}
    >
      {attributes.map(({ attributeKey, label, format }) => {
        const designationAttrValue = getAttributeDisplayValue(designationAttributes, attributeKey, format);

        const comparativeDesignationAttrValue =
          comparativeDesignationAttributes &&
          getAttributeDisplayValue(comparativeDesignationAttributes, attributeKey, format);

        return (
          <div key={attributeKey} className="flex flex-col gap-y-1">
            <h2 className="text-xs font-semibold text-neutral-800">{label}</h2>
            <Row
              printable={printable}
              displayName={designation.display_name!}
              value={designationAttrValue ?? undefined}
              color={colorAccessor(designation.id)}
            />
            {isDefined(comparativeDesignation) && isDefined(comparativeDesignationAttrValue) && (
              <Row
                printable={printable}
                displayName={comparativeDesignation.display_name!}
                value={comparativeDesignationAttrValue}
                color={colorAccessor(comparativeDesignation.id)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

interface RowProps {
  displayName: string;
  value?: string;
  color: Color;
  printable?: boolean;
}

function Row({ displayName, value, color, printable = false }: RowProps) {
  return (
    <div
      className={clsx("flex flex-row justify-between gap-x-1 items-start", {
        "flex-nowrap": !printable,
      })}
    >
      <div
        className={clsx("text-xs text-neutral-800 border-l-[3px] pl-1 border-solid", {
          truncate: !printable,
        })}
        style={{
          borderLeftColor: color.toString(),
        }}
      >
        {displayName}
      </div>
      {isDefined(value) ? (
        <div className="text-xs font-semibold text-right">{value}</div>
      ) : (
        <div className="text-xs font-semibold text-neutral-400">-</div>
      )}
    </div>
  );
}

function getAttributeDisplayValue(
  designationAttributes: SingleDesignationDesignationAttribute[],
  attributeKey: string,
  format: ValueFormatter | undefined
) {
  const designationAttrValueNumeric = getAttributeNumericValue(designationAttributes, attributeKey);
  return format && isNumber(designationAttrValueNumeric)
    ? format(designationAttrValueNumeric)
    : getAttributeValue(designationAttributes, attributeKey);
}
